import { Utils } from '@/common/Utils';
import { PAGE_SIZE_OPTIONS } from '@/constants';
import MoreVertIcon from '@/resources/images/icons-more-vertical.svg';
import { KLAYO_COLORS } from '@/themes';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import User1Icon from '../resources/images/icons-users-1.svg';
import { TablePlaceholder } from './TablePlaceholder';
import { EnrolmentStatusText } from './EnrolmentStatusText';
import { CourseStudentRow } from './table/CourseStudentsTable';

interface EnrolmentCardListProps {
  rows: CourseStudentRow[];
  totalCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  translate: (key: string, params?: any) => string;
  isLoading?: boolean;
  onStudentAction?: (event: React.MouseEvent, row: CourseStudentRow) => void;
}

export const EnrolmentCardList = ({
  rows,
  totalCount,
  paginationModel,
  onPaginationModelChange,
  translate,
  isLoading,
  onStudentAction,
}: EnrolmentCardListProps) => {
  return (
    <Box>
      {isLoading ? (
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress size={32} />
        </Box>
      ) : rows.length === 0 ? (
        <Box py={4}>
          <TablePlaceholder
            text={{
              primary: translate('manage.courseStudents.nothingToShow'),
              secondary: translate('manage.courseStudents.manageEnrolmentsHint'),
            }}
            icon={<User1Icon />}
          />
        </Box>
      ) : (
        rows.map((row, index) => (
          <Box key={row.id}>
            <Stack sx={{ px: 0, py: 2 }} gap={0.5}>
              <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <Typography variant='body1' fontWeight={600}>
                  {row.name}
                </Typography>
                <IconButton size='small' onClick={(e) => onStudentAction?.(e, row)}>
                  <MoreVertIcon width={20} height={20} />
                </IconButton>
              </Stack>
              {row.enrolledDate && (
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR600}>
                  {translate('manage.courseStudents.enrolmentDate')}:{' '}
                  {Utils.formatDate(row.enrolledDate)}
                </Typography>
              )}
              <Stack direction='row' gap={0.5} alignItems='center' flexWrap='wrap'>
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR600}>
                  {translate('manage.courseStudents.status')}:
                </Typography>
                <EnrolmentStatusText
                  status={row.status}
                  isOverdue={row.isOverdue}
                  translate={translate}
                />
              </Stack>
              <LinearProgress
                variant='determinate'
                value={row.progress ?? 0}
                sx={{
                  mt: 0.5,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: KLAYO_COLORS.GreyGR100,
                  '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: '#4CAF50' },
                }}
              />
            </Stack>
            {index < rows.length - 1 && <Divider />}
          </Box>
        ))
      )}

      <TablePagination
        component='div'
        count={totalCount}
        page={paginationModel.page}
        rowsPerPage={paginationModel.pageSize}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        onPageChange={(_e, newPage) => {
          if (isLoading) return;
          onPaginationModelChange({ ...paginationModel, page: newPage });
        }}
        onRowsPerPageChange={(e) => {
          onPaginationModelChange({ page: 0, pageSize: Number(e.target.value) });
        }}
        labelRowsPerPage={translate('pagination.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          translate('pagination.displayedRows', {
            from,
            to,
            count: count !== -1 ? count : `>${to}`,
          })
        }
        sx={{
          '& .MuiToolbar-root': {
            padding: 0,
          },

          '& .MuiToolbar-root .MuiTablePagination-actions': {
            marginLeft: 2,
          },

          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
            display: { xs: 'none', sm: 'block' },
          },
        }}
      />
    </Box>
  );
};
